import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Header from './components/Header';
import FeeCalculator from './pages/FeeCalculator';
import Settings from './pages/Settings';
import CompetitorsList from './pages/CompetitorsList';
import CompetitorForm from './pages/CompetitorForm';
import BreakpointIndicator from './components/BreakpointIndicator';
import ApiService from './services/apiService';
import './App.css';

// Hilfsfunktion um CSS-Variable in HEX oder RGB-Farbe umzuwandeln
const getCssVariableValue = (variableName) => {
  // Die Standardfarben für den Fall, dass CSS-Variablen nicht geladen werden können
  const defaultColors = {
    '--primary-main': '#2A3192',
    '--primary-light': '#535BB5',
    '--primary-dark': '#1E2470',
    '--secondary-main': '#00AAE3',
    '--secondary-light': '#41C0EA',
    '--secondary-dark': '#0086B3',
    '--accent': '#FF8300',
    '--accent-light': '#FFA040',
    '--accent-dark': '#CC6900',
    '--error': '#F44336',
    '--warning': '#FFC107',
    '--info': '#2196F3',
    '--success': '#4CAF50',
  };

  // Versuche, die CSS-Variable vom root-Element zu holen
  const cssValue = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  
  // Wenn ein Wert gefunden wurde, verwende ihn; ansonsten nutze den Standardwert
  return cssValue || defaultColors[variableName] || '#000000';
};

function App() {
  const [darkMode, setDarkMode] = useState(true); // Dark mode ist standardmäßig aktiviert
  const [basePrice, setBasePrice] = useState(16.36); // Zustand für den Basispreis
  const [newFee, setNewFee] = useState({ percentage_fee: 3.75, fixed_fee: 0.85 }); // Zustand für die neue Gebühr
  const [showNewFee, setShowNewFee] = useState(true); // Zustand für die Anzeige der neuen Gebühr
  
  // Initialisiere den localStorage
  useEffect(() => {
    // Initialisiere den ApiService
    ApiService.init();
    console.log('ApiService initialisiert, localStorage vorbereitet');
  }, []);
  
  // Theme basierend auf dem Dark/Light-Mode erstellen
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        // Im Dark Mode die spezielle CTA-Farbe verwenden, im Light Mode die normale primary-main
        main: darkMode ? getCssVariableValue('--cta-dark-mode') : getCssVariableValue('--primary-main'),
        light: getCssVariableValue('--primary-light'),
        dark: getCssVariableValue('--primary-dark'),
      },
      secondary: {
        main: getCssVariableValue('--secondary-main'),
        light: getCssVariableValue('--secondary-light'),
        dark: getCssVariableValue('--secondary-dark'),
      },
      accent: {
        main: getCssVariableValue('--accent'),
        light: getCssVariableValue('--accent-light'),
        dark: getCssVariableValue('--accent-dark'),
      },
      error: {
        main: getCssVariableValue('--error'),
      },
      warning: {
        main: getCssVariableValue('--warning'),
      },
      info: {
        main: getCssVariableValue('--info'),
      },
      success: {
        main: getCssVariableValue('--success'),
      },
    },
    typography: {
      fontFamily: "'Roboto', sans-serif",
      h1: {
        fontFamily: "'Quicksand', sans-serif",
        fontWeight: 700,
      },
      h2: {
        fontFamily: "'Quicksand', sans-serif",
        fontWeight: 700,
      },
      h3: {
        fontFamily: "'Quicksand', sans-serif",
        fontWeight: 600,
      },
      h4: {
        fontFamily: "'Quicksand', sans-serif",
        fontWeight: 600,
      },
      h5: {
        fontFamily: "'Quicksand', sans-serif",
        fontWeight: 500,
      },
      h6: {
        fontFamily: "'Quicksand', sans-serif",
        fontWeight: 500,
      },
    },
  });

  // Dark Mode-Präferenz im localStorage speichern
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    // CSS-Klasse für Dark Mode am body-Element hinzufügen/entfernen
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);
  
  // Basispreis im localStorage speichern
  useEffect(() => {
    localStorage.setItem('basePrice', JSON.stringify(basePrice));
  }, [basePrice]);
  
  // Neue Gebühr im localStorage speichern
  useEffect(() => {
    localStorage.setItem('newFee', JSON.stringify(newFee));
  }, [newFee]);
  
  // Status der neuen Gebühr im localStorage speichern
  useEffect(() => {
    localStorage.setItem('showNewFee', JSON.stringify(showNewFee));
  }, [showNewFee]);
  
  // Dark Mode-Präferenz und andere Einstellungen beim App-Start aus localStorage laden
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      setDarkMode(JSON.parse(savedMode));
    }
    
    const savedBasePrice = localStorage.getItem('basePrice');
    if (savedBasePrice !== null) {
      setBasePrice(JSON.parse(savedBasePrice));
    }
    
    const savedNewFee = localStorage.getItem('newFee');
    if (savedNewFee !== null) {
      setNewFee(JSON.parse(savedNewFee));
    }
    
    const savedShowNewFee = localStorage.getItem('showNewFee');
    if (savedShowNewFee !== null) {
      setShowNewFee(JSON.parse(savedShowNewFee));
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          <main className="content">
            <Routes>
              <Route path="/" element={<FeeCalculator 
                basePrice={basePrice} 
                setBasePrice={setBasePrice} 
                newFee={newFee}
                setNewFee={setNewFee}
                showNewFee={showNewFee}
                setShowNewFee={setShowNewFee}
              />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/competitors" element={<CompetitorsList />} />
              <Route path="/competitors/new" element={<CompetitorForm />} />
              <Route path="/competitors/edit/:id" element={<CompetitorForm />} />
            </Routes>
          </main>
          {/* Breakpoint-Indikator für Debugging */}
          <BreakpointIndicator />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
